import DOMPurify from 'dompurify';

/**
 * Note bodies come in two shapes, both of which must render:
 *
 *  - the legacy format: plain text where each newline is a paragraph break,
 *    which may ALSO contain inline HTML (legacy notes were written in a rich
 *    text editor that serialized to b/i/u/strike/code, headings, lists, links,
 *    blockquotes and <br>);
 *  - plain HTML bodies pasted or synced whole.
 *
 * The legacy app unified them with one transform — wrap the body in <p> and
 * turn every newline into a paragraph break, then parse the result as HTML
 * (see legacy/bcecloud/src/components/editors/RichText.jsx). Replicating that
 * transform byte-for-byte keeps every existing note rendering exactly as it
 * did in the old app; the browser's parser normalises any odd nesting the
 * split produces, just as the legacy DOMParser did.
 */

/** The tags the legacy editor could produce (plus strong/em equivalents). */
const LEGACY_TAGS = [
  'p', 'br', 'span', 'a', 'blockquote', 'code',
  'b', 'strong', 'i', 'em', 'u', 'strike', 's',
  'h1', 'h2', 'h3', 'h4', 'h5',
  'ol', 'ul', 'li',
];

/**
 * Converts a note body in the legacy format to sanitized HTML.
 *
 * @param body - The raw note body (plain text, legacy rich text, or HTML).
 * @returns Sanitized HTML, newlines rendered as paragraph breaks.
 */
export function legacyNoteHtml(body: string): string {
  // The legacy transform, verbatim: newlines become paragraph boundaries.
  const html = `<p>${body.split('\n').join('</p><p>')}</p>`;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: LEGACY_TAGS,
    // href only; DOMPurify strips javascript: and friends by default.
    ALLOWED_ATTR: ['href'],
  });
}

export interface NoteBodyProps {
  /** The raw note body. */
  readonly body: string;
  readonly className?: string;
}

/**
 * Renders a note body, supporting both HTML notes and the legacy
 * newline-paragraph format.
 *
 * @param props - The raw body and optional class.
 * @returns The formatted note body.
 */
export function NoteBody({ body, className }: NoteBodyProps): React.ReactElement {
  return (
    <div
      className={className ?? 'note-prose mt-2 text-portal-small'}
      // Sanitized by legacyNoteHtml (DOMPurify, legacy tag allowlist).
      dangerouslySetInnerHTML={{ __html: legacyNoteHtml(body) }}
    />
  );
}
